import type { WalletHistory } from '@/services/api'

export interface GroupedWalletHistory extends WalletHistory {
  feeEntry?: WalletHistory
}

const PAYMASTER_TREASURY_ADDRESS = (
  import.meta.env.VITE_PAYMASTER_TREASURY_ADDRESS ?? ''
).toLowerCase()

/**
 * 同じ txHash を持つ送金履歴をグルーピングする。
 * - toAddress が PAYMASTER_TREASURY_ADDRESS と一致 = 手数料エントリ
 * - それ以外 = メインエントリ（送金本体）
 * - order 付き（購入履歴）はグルーピング対象外
 * - onchainHistory なしのエントリもそのまま
 */
export const groupHistoryByTxHash = (entries: WalletHistory[]): GroupedWalletHistory[] => {
  const txHashMap = new Map<string, { main?: WalletHistory; fee?: WalletHistory }>()
  const result: GroupedWalletHistory[] = []

  for (const entry of entries) {
    // order 付き or onchainHistory なし → グルーピング対象外
    if (entry.order || !entry.onchainHistory?.txHash) {
      result.push(entry)
      continue
    }

    // PAYMASTER_TREASURY_ADDRESS が未設定 → グルーピング不可
    if (!PAYMASTER_TREASURY_ADDRESS) {
      result.push(entry)
      continue
    }

    const txHash = entry.onchainHistory.txHash
    const toAddress = entry.onchainHistory.toAddress ?? ''
    const isFee = toAddress.toLowerCase() === PAYMASTER_TREASURY_ADDRESS
    const existing = txHashMap.get(txHash)

    if (!existing) {
      txHashMap.set(txHash, isFee ? { fee: entry } : { main: entry })
    } else {
      if (isFee) {
        existing.fee = entry
      } else {
        if (existing.main) {
          console.warn(`[historyGrouping] duplicate main entry for txHash: ${txHash}`)
        }
        existing.main = entry
      }
    }
  }

  // txHashMap の結果をマージ
  for (const { main, fee } of txHashMap.values()) {
    if (main) {
      // fee が別ページにある場合は手数料なしで表示（許容）
      result.push({ ...main, feeEntry: fee })
    } else if (fee) {
      // メインがない場合（ページネーション境界など）はそのまま表示
      result.push(fee)
    }
  }

  // createdAt 降順でソート
  result.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())

  return result
}
